import axios from 'axios';
import { API } from '../App';

/**
 * QR Pool API Utilities
 * Common functions for interacting with QR Pool endpoints
 */

// ============ UTILITY FUNCTIONS ============

/**
 * Normalize phone number to 10 digits (India format)
 * Removes all non-digit characters and handles different formats
 */
export const normalizePhone = (phone) => {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // If it has country code (91 for India), remove it to get 10 digits
  if (digitsOnly.startsWith('91') && digitsOnly.length === 12) {
    return digitsOnly.substring(2);
  }
  // If already 10 digits, return as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  // If has more than 10 digits, take last 10
  if (digitsOnly.length > 10) {
    return digitsOnly.slice(-10);
  }
  // Return as is (validation will handle any issues)
  return digitsOnly;
};

// ============ OTP API ============

/**
 * Send OTP to phone number
 */
export const sendOTP = async (phone) => {
  try {
    const normalizedPhone = normalizePhone(phone);
    const payload = {
      phone: normalizedPhone
    };
    
    console.log('[sendOTP] Original phone:', phone, '-> Normalized:', normalizedPhone);
    console.log('[sendOTP] API endpoint:', `${API}/auth/send-otp`);
    
    const response = await axios.post(`${API}/auth/send-otp`, payload);
    
    console.log('[sendOTP] Success response:', response.data);
    return response.data;
  } catch (error) {
    const errorData = error.response?.data || error;
    console.error('[sendOTP] Error:', {
      status: error.response?.status,
      detail: errorData?.detail || errorData?.message || errorData,
      fullError: errorData
    });
    throw errorData;
  }
};

/**
 * Verify OTP and authenticate user
 */
export const verifyOTP = async (phone, otp) => {
  try {
    const normalizedPhone = normalizePhone(phone);
    const payload = {
      phone: normalizedPhone,
      otp: otp.trim()
    };
    
    console.log('[verifyOTP] Original phone:', phone, '-> Normalized:', normalizedPhone);
    console.log('[verifyOTP] OTP:', payload.otp);
    console.log('[verifyOTP] API endpoint:', `${API}/auth/verify-otp`);
    
    const response = await axios.post(`${API}/auth/verify-otp`, payload);
    
    console.log('[verifyOTP] Success response:', response.data);
    
    // Store auth token
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  } catch (error) {
    const errorData = error.response?.data || error;
    console.error('[verifyOTP] Error:', {
      status: error.response?.status,
      detail: errorData?.detail || errorData?.message || errorData,
      fullError: errorData
    });
    throw errorData;
  }
};

// Export OTP functions together
export const otpApi = {
  sendOTP,
  verifyOTP
};

// Public endpoints (no auth)
export const qrPoolApi = {
  /**
   * Get QR information
   */
  getQRInfo: async (qrId) => {
    try {
      const response = await axios.get(`${API}/q/${qrId}/info`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Get QR status and next action
   */
  getQRStatus: async (qrId) => {
    try {
      const response = await axios.get(`${API}/q/${qrId}/status`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Claim a QR code
   */
  claimQR: async (qrId, phone, vehicleNumber) => {
    try {
      const response = await axios.post(`${API}/q/${qrId}/claim`, {
        phone,
        vehicle_number: vehicleNumber
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Renew QR subscription
   */
  renewQR: async (qrId, renewalDays = 365) => {
    try {
      const response = await axios.post(`${API}/q/${qrId}/renew`, {
        renewal_days: renewalDays
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

// Admin endpoints (requires auth)
export const qrPoolAdminApi = {
  /**
   * Generate batch of QRs
   */
  generateBatch: async (quantity) => {
    try {
      const response = await axios.post(
        `${API}/admin/qr-pool/generate`,
        { quantity },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * List QRs with filtering
   */
  listQRs: async (options = {}) => {
    try {
      const {
        status = null,
        batchId = null,
        limit = 100,
        skip = 0
      } = options;

      let url = `${API}/admin/qr-pool/list?limit=${limit}&skip=${skip}`;
      if (status) url += `&status=${status}`;
      if (batchId) url += `&batch_id=${batchId}`;

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Disable a QR code
   */
  disableQR: async (qrId, reason = null) => {
    try {
      let url = `${API}/admin/qr-pool/${qrId}/disable`;
      if (reason) url += `?reason=${encodeURIComponent(reason)}`;

      const response = await axios.post(url, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Enable a disabled QR code
   */
  enableQR: async (qrId) => {
    try {
      const response = await axios.post(
        `${API}/admin/qr-pool/${qrId}/enable`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Extend QR expiry
   */
  extendQR: async (qrId, days = 365) => {
    try {
      const response = await axios.post(
        `${API}/admin/qr-pool/${qrId}/extend?days=${days}`,
        {},
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Export QRs as CSV
   */
  exportCSV: async (options = {}) => {
    try {
      const {
        batchId = null,
        statusFilter = null
      } = options;

      const response = await axios.post(
        `${API}/admin/qr-pool/export/csv`,
        {
          batch_id: batchId,
          status_filter: statusFilter
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

/**
 * Determine stable frontend URL for QR code generation
 */
const getStableFrontendUrl = () => {
  if (process.env.REACT_APP_FRONTEND_URL) {
    return process.env.REACT_APP_FRONTEND_URL.trim();
  }

  const hostname = window.location.hostname;

  // Production
  if (
    hostname === 'app.sideo.in' ||
    hostname === 'sideo.in' ||
    hostname === 'www.sideo.in' ||
    hostname === 'sideo-pre-qr.vercel.app'
  ) {
    return 'https://app.sideo.in';
  }

  // Local dev
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }

  // Staging/preview frontend deployments should use their own frontend origin
  if (hostname.includes('vercel.app')) {
    return window.location.origin;
  }

  return window.location.origin;
};

/**
 * Generate QR activation URL
 * Uses provided baseUrl, or determines stable frontend URL
 */
export const generateQRUrl = (qrId, baseUrl = null) => {
  const url = baseUrl || getStableFrontendUrl();
  return `${url}/q/${qrId}`;
};

/**
 * Format expiry date for display
 */
export const formatExpiryDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Get days until expiry
 */
export const daysUntilExpiry = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Get status badge color
 */
export const getStatusColor = (status) => {
  const colors = {
    unclaimed: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    expired: 'bg-orange-100 text-orange-800',
    disabled: 'bg-red-100 text-red-800',
    claimed: 'bg-blue-100 text-blue-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};
